"use client"

import { useEffect, useState } from "react"
import type { WaitEstimate } from "./waitEstimate"

export const WAIT_REFRESH_MS = 90_000

type RefreshDeps = {
  fetchImpl: typeof fetch
  setIntervalImpl: (fn: () => void, ms: number) => unknown
  clearIntervalImpl: (id: unknown) => void
  doc: Pick<Document, "addEventListener" | "removeEventListener" | "visibilityState">
}

/** Refresca el estimado cada 90 s y al volver a la pestaña. Devuelve el cleanup. Exportado para testear sin DOM. */
export function startWaitRefresh(slug: string, onEstimate: (e: WaitEstimate) => void, deps: RefreshDeps): () => void {
  let cancelled = false
  const refresh = async () => {
    try {
      const res = await deps.fetchImpl(`/api/online/wait-estimate?slug=${encodeURIComponent(slug)}`, { cache: "no-store" })
      if (!res.ok) return
      const body = (await res.json()) as { estimate?: WaitEstimate }
      if (!cancelled && body.estimate && typeof body.estimate.minutes === "number") onEstimate(body.estimate)
    } catch {
      // se queda con el último valor bueno
    }
  }
  const id = deps.setIntervalImpl(() => void refresh(), WAIT_REFRESH_MS)
  const onVisible = () => {
    if (deps.doc.visibilityState === "visible") void refresh()
  }
  deps.doc.addEventListener("visibilitychange", onVisible)
  return () => {
    cancelled = true
    deps.clearIntervalImpl(id)
    deps.doc.removeEventListener("visibilitychange", onVisible)
  }
}

/**
 * El tiempo de espera vigente de la tienda (PR K). Arranca con el que calculó el
 * servidor al renderizar y, SOLO si la tienda tiene `live_wait_time`, lo refresca
 * cada 90 s y al volver a la pestaña. Apagado: devuelve `initial` tal cual, sin
 * fetch ni timers. Un refresco fallido deja el último valor bueno.
 */
export function useWaitEstimate(initial: WaitEstimate, slug: string, enabled: boolean): WaitEstimate {
  const [estimate, setEstimate] = useState(initial)

  useEffect(() => {
    if (!enabled) return
    return startWaitRefresh(slug, setEstimate, {
      fetchImpl: fetch,
      setIntervalImpl: (fn, ms) => setInterval(fn, ms),
      clearIntervalImpl: (id) => clearInterval(id as ReturnType<typeof setInterval>),
      doc: document,
    })
  }, [enabled, slug])

  return enabled ? estimate : initial
}
